import { ActionIcon, Text, TextInput } from '@mantine/core';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon/Icon';
import { AvatarInfoRow } from '@dealstack/frontend/shared/components/AvatarInfoRow/AvatarInfoRow';
import { PermissionsCell } from '@dealstack/frontend/shared/components/PermissionCell/PermissionsCell';
import {
	ExtendedColumn,
	ExtendedColumnDef,
} from '@dealstack/frontend/shared/components/Table/MantineTable/useMantineTableWithPaginationFilteringSorting';
import { userTableAccessorFn } from '@dealstack/frontend/shared/utils/searchUtils';
import { formatUserFullName } from '@dealstack/shared/lib/format/user';
import { PermissionRoleWithOwnerEnum } from '@dealstack/shared/lib/generated';
import { TableFilterCheckboxSelect } from '@dealstack/frontend/shared/components/Table/MantineTable/TableFilterCheckboxSelect';
import { TranslateFunction } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.types';
import { I18Namespaces } from '@dealstack/shared/lib/common/i18n/i18n.ts';

interface UserWithNameAndEmail {
	firstName?: string;
	lastName?: string;
	email?: string;
}

interface UserWithPermissions extends UserWithNameAndEmail {
	permissionRoles: PermissionRoleWithOwnerEnum[];
}

interface CreateUserNameColumnParams {
	header: string;
	filterPlaceholder: string;
	filterText: string;
}

export const createUserNameColumn = <T extends UserWithNameAndEmail>({
	header,
	filterPlaceholder,
	filterText,
}: CreateUserNameColumnParams): ExtendedColumnDef<T> => {
	return {
		cell: ({ row }) => {
			const { firstName, lastName, email } = row.original;
			const name = formatUserFullName(row.original);

			return (
				<AvatarInfoRow
					firstName={firstName}
					lastName={lastName}
					subText={email}
					text={name}
					avatarSize="2.5rem"
				/>
			);
		},
		filter: ({ column }) => (
			<TextInput
				value={(column.getFilterValue() || '') as string}
				onChange={(e) => column.setFilterValue(e.currentTarget.value)}
				placeholder={filterPlaceholder}
				rightSection={
					!!column.getFilterValue() && (
						<ActionIcon variant="transparent" onClick={() => column.setFilterValue('')}>
							<Icon variant="close-v3" size="lg" />
						</ActionIcon>
					)
				}
			/>
		),
		header: () => <Text fw={600}>{header}</Text>,
		columnLabel: header,
		id: 'name',
		accessorFn: userTableAccessorFn,
		enableGlobalFilter: true,
		showFilter: true,
		filterText,
		meta: { widthPercentage: 40 },
	};
};

interface CreateUserPermissionsColumnParams {
	t:
		| TranslateFunction<I18Namespaces.MIP_CREATE>
		| TranslateFunction<I18Namespaces.ORGANIZATION>
		| TranslateFunction<I18Namespaces.ADMIN_DASHBOARD>;
	header: string;
	id: string;
	filterPlaceholder: string;
	filterText: string;
	permissions: { label: string; value: string }[];
}

export const createUserPermissionsColumn = <T extends UserWithPermissions>({
	t,
	header,
	filterPlaceholder,
	filterText,
	permissions,
	id,
}: CreateUserPermissionsColumnParams): ExtendedColumnDef<T> => {
	return {
		id,
		cell: ({ row }) => <PermissionsCell permissionRoles={row.original.permissionRoles} t={t} />,
		header: () => <Text fw={600}>{header}</Text>,
		columnLabel: header,
		filterText,
		filter: ({ column }) => (
			<TableFilterCheckboxSelect
				column={column as ExtendedColumn<unknown>}
				filterText={filterPlaceholder}
			/>
		),
		filterFn: (row, columnId, filterValue) => {
			if (!filterValue) {
				return true;
			}
			return row.original.permissionRoles.includes(
				filterValue as PermissionRoleWithOwnerEnum
			);
		},
		showFilter: true,
		enableGlobalFilter: true,
		mantineFilterSelectProps: {
			data: permissions,
		},
		meta: { isGrow: true },
	};
};
